import React, { memo, useCallback, useMemo } from 'react';

import { cloneDeep, omit } from 'lodash';
import { Button, Form, useForm, RouteCostFormSection, TypographyTitle } from '@bringg/react-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { PrivilegeTypes, VehicleType, CostTypeEnum } from '@bringg/types';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { useHasFeatureFlag } from '@bringg-frontend/hooks';

import notification from 'bringg-web/services/notification';
import { useHasAccess } from 'bringg-web/utils/privileges';
import { getDistanceUnit } from 'bringg-web/utils/distance';
import { toDisplaySpeedFactor, toStorageSpeedFactor } from 'bringg-web/utils/speed-factor-conversions';
import TitleSection from './vehicle-profile-form/title-section';
import GeneralInfoSection from './vehicle-profile-form/general-info-section';
import RoadRestrictionsSection from './vehicle-profile-form/road-restrictions-section';
import CapacityRestrictionsSection from './vehicle-profile-form/capacity-restrictions-section';
import useStores from './../../recipes/use-stores';
import { useRouteCostTranslations } from '../../hooks/use-route-cost-translations';

import './vehicle-profile-form.scss';

const RouteCostSectionHeader = memo(() => {
	const { t } = useTranslation();

	return (
		<div className="vehicle-profiles-form--route-cost-section-header">
			<TypographyTitle level={4} className="vehicle-profiles-form--section-icon">
				<BringgIcon iconName={BringgFontIcons.Route} />
			</TypographyTitle>
			<TypographyTitle level={4} className="vehicle-profiles-form--section-title">
				{t('VEHICLE_PROFILES.FORM.ROUTE_COST')}
			</TypographyTitle>
		</div>
	);
});

const VehicleProfileForm = ({ vehicleType, setVehicleType, vehicleCategories }) => {
	const {
		vehicleTypesStore,
		merchantConfigurationsStore: { merchantCountry }
	} = useStores();
	const { t } = useTranslation();
	const [form] = useForm();
	const routeCostCalculationEnabled = useHasFeatureFlag('enable_route_cost_calculation');
	const editRouteCostCalculationAllowed = useHasAccess(PrivilegeTypes.ALLOW_DISPATCHER_EDIT_ROUTE_COST_CALCULATION);
	const isCreateForm = !vehicleType?.id;

	const routeCostTranslations = useRouteCostTranslations();

	const trimRedundantSpaces = (record: VehicleType): VehicleType => ({
		...record,
		title: record.title.trim(),
		description: record.description?.trim(),
		external_id: record.external_id?.trim()
	});

	const reassignVehicleCategory = (value: VehicleType) => {
		if (typeof value?.vehicle_category === 'string') {
			return value;
		}

		return omit({ ...value, vehicle_category_id: value.vehicle_category }, 'vehicle_category');
	};

	const saveVehicleType = async record => {
		if (isCreateForm) {
			try {
				await vehicleTypesStore.create(record);

				notification.success(t('VEHICLE_PROFILES.TOAST.CREATE.SUCCESS', { title: record.title }));
			} catch {
				notification.error(t('VEHICLE_PROFILES.TOAST.CREATE.ERROR', { title: record.title }));
			}
		} else {
			try {
				await vehicleTypesStore.update(record.id, record);

				notification.success(t('VEHICLE_PROFILES.TOAST.UPDATE.SUCCESS', { title: record.title }));
			} catch {
				notification.error(t('VEHICLE_PROFILES.TOAST.UPDATE.ERROR', { title: record.title }));
			}
		}
	};

	const getValidatedValues = useCallback(async () => {
		let values = null;
		try {
			values = await form
				.validateFields()
				.then(validatedValues => {
					return routeCostCalculationEnabled ? form.getFieldsValue(true) : validatedValues;
				})
				.catch(() => {
					return null;
				});
		} catch (err) {
			console.error('Validate Failed:', err);
		}

		return values;
	}, [form, routeCostCalculationEnabled]);

	const resetValues = useCallback(() => {
		setVehicleType({
			id: isCreateForm ? null : vehicleType.id,
			handling_units: {}
		} as unknown as VehicleType);

		form.setFieldsValue({
			title: '',
			description: '',
			default_for_merchant: false,
			external_id: '',
			vehicle_category: '',
			cost_per_shift: null,
			is_green: false,
			is_trailer: false,
			can_attach_trailer: false,
			max_route_distance_km: null,
			speed_factor: null,
			// The normalization for `handling_units` is needed only because the form stores this field as an object,
			// where both key and value represent the displayed value.
			// We should consider refactoring the form to work with primitives instead.
			// This would eliminate the need for additional normalization and simplify the data handling.
			handling_units: '',
			max_total_weight: null,
			maximum_tasks_per_route: null,
			height: null,
			width: null,
			length: null,
			route_cost_calculation_type: CostTypeEnum.OVERALL_COST_PER_MINUTE,
			route_cost_calculation_configuration: null
		});
	}, [setVehicleType, form, isCreateForm, vehicleType.id]);

	const handleSubmit = useCallback(
		async (shouldSubmitAndContinue: boolean) => {
			const values = await getValidatedValues();
			if (!values) return;

			const vehicleTypeData = {
				...values,
				id: isCreateForm ? null : vehicleType.id,
				external_id: values.external_id || null
			};

			const vehicleTypeDataTrimmed = trimRedundantSpaces(vehicleTypeData);
			const vehicleTypeDataWithConvertedSpeedFactor = toStorageSpeedFactor(vehicleTypeDataTrimmed);
			const submitData = reassignVehicleCategory(vehicleTypeDataWithConvertedSpeedFactor);

			await saveVehicleType(submitData);

			if (shouldSubmitAndContinue) {
				form.resetFields();
				setVehicleType({});
			} else {
				setVehicleType(null);
			}
		},
		[saveVehicleType, setVehicleType, getValidatedValues, form, vehicleType.id, isCreateForm]
	);

	const existingVehicleProfilesTitles = useMemo(
		() => vehicleTypesStore.all.filter(({ id }) => id !== vehicleType?.id).map(({ title }) => title?.toLowerCase()),
		[vehicleTypesStore.all, vehicleType?.id]
	);

	const initialValues = useMemo(() => {
		if (!vehicleType) {
			return null;
		}

		// TODO: the whole form and table of VehicleProfile should be refactored
		// to use vehicleTypeId in all places and not vehicleType object
		// and then grab vehicleType from vehicleTypesStore by ID
		// this is a temporary fix for customer bug
		const initialVehicleTypeValues = cloneDeep(vehicleType);
		const initialValuesWithConvertedSpeedFactor = toDisplaySpeedFactor(initialVehicleTypeValues);

		return initialValuesWithConvertedSpeedFactor;
	}, [vehicleType]);

	return (
		<Form form={form} initialValues={initialValues}>
			<div className="vehicle-profiles-form-wrapper">
				<TitleSection
					form={form}
					resetValues={resetValues}
					existingVehicleProfilesTitles={existingVehicleProfilesTitles}
				/>
				<div className="vehicle-profiles-page--form-wrapper">
					<GeneralInfoSection vehicleType={vehicleType} vehicleCategories={vehicleCategories} form={form} />
					<RoadRestrictionsSection />
					<CapacityRestrictionsSection />
					{routeCostCalculationEnabled ? (
						<RouteCostFormSection
							containerClassName="vehicle-profiles-form--section-wrapper flex-column"
							bodyClassName="vehicle-profiles-form--route-cost-section-body"
							childFieldsFormKey="route_cost_calculation_configuration"
							distanceUnit={getDistanceUnit(merchantCountry)}
							translations={routeCostTranslations}
							header={<RouteCostSectionHeader />}
							disabled={!editRouteCostCalculationAllowed}
						/>
					) : null}
				</div>
				<div className="vehicle-profiles-page--form-controls-wrapper">
					<Button type="link" onClick={() => setVehicleType(null)}>
						{t('VEHICLE_PROFILES.FORM.DISCARD')}
					</Button>
					<div className="vehicle-profiles-page--form-controls-wrapper-right">
						{isCreateForm && (
							<Button className="save-and-create-new-btn" onClick={() => handleSubmit(true)}>
								{t('VEHICLE_PROFILES.FORM.SAVE_AND_CREATE')}
							</Button>
						)}
						<Button onClick={() => handleSubmit(false)}>
							{' '}
							{isCreateForm ? t('VEHICLE_PROFILES.FORM.CREATE') : t('VEHICLE_PROFILES.FORM.UPDATE')}
						</Button>
					</div>
				</div>
			</div>
		</Form>
	);
};

export default observer(VehicleProfileForm);
